import { ComponentChild, RefObject } from 'preact';
import { ForwardedRef, forwardRef } from 'preact/compat';
import { Offset, WindowOverlay, WindowOverlayPlacement } from '../UNSAFE_WindowOverlay';
import { TestIdProps, useTestId } from '../hooks/UNSAFE_useTestId';
import { UseAnimationConfig, useAnimation } from '../hooks/UNSAFE_useAnimation';
import { useEffect, useState } from 'preact/hooks';

import { DialogRedwoodTheme } from './themes/redwood/DialogTheme';
import { DialogStyles } from './themes/DialogStyles.css';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { Layer } from '../UNSAFE_Layer';
import { Modal } from '../UNSAFE_Modal';
import { classNames } from '../utils/UNSAFE_classNames';
import { globalImages } from '#/ImageVars.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useId } from '../hooks/UNSAFE_useId';

// animation states
type Status = 'opening' | 'closing' | 'unmounted';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children' | 'role'>;

type OnCloseDetail = {
  reason: 'escapeKey';
};

type DialogProps = IntrinsicProps & {
  /**
   * Optional ref for an element to focus on when component mounts
   */
  autoFocusRef?: RefObject<HTMLElement>;

  /**
   * On Close callback.
   */
  onClose?: (detail: OnCloseDetail) => void;

  /**
   * On opening or closing transition end callback.
   */
  onTransitionEnd?: (value: boolean) => void;

  /**
   * Determines whether the Dialog is open.
   */
  isOpen: boolean;

  /**
   * Dialog header content. Can be a string or VNode (Component Child).
   * <p>
   * When a plain string is passed, it will be used as the dialog title. Otherwise, the prop value
   * will be used as the header content.
   * </p>
   * <p>
   * Dialog will automatically refer to the header content as the accessible label.
   * The user can override this behavior by specifying the <code>aria-labelledby</code> property
   * and setting its value as needed.
   * </p>
   */
  header?: ComponentChild;

  /**
   * Dialog footer content.
   */
  footer?: ComponentChild;

  /**
   * Determines dialog modality. Default is 'modal'.
   */
  modality?: 'modal' | 'modeless';

  /**
   * Determines placement of the dialog relative to the viewport.
   * Supported values are:
   * <p> <code>center</code>, <code>top</code>, <code>top-start</code>, <code>top-end</code>,
   * <code>end</code>, <code>end-top</code>, <code>end-bottom</code>,
   * <code>bottom</code>, <code>bottom-start</code>, <code>bottom-end</code>,
   * <code>start</code>, <code>start-top</code>, <code>start-bottom</code>
   * </p>
   * Default is <code>center</code>.
   */
  placement?: WindowOverlayPlacement;

  /**
   * Determines the offset of the Dialog from its core placement along the specified axes.
   *
   * Offset could be a number or an object
   *
   * An object has the following type:
   * <pre>
   *   {
   *     mainAxis?: number;
   *     crossAxis?: number;
   *   }
   * </pre>
   * For the <code>center</code> placement the convention for the offset type is that mainAxis defines
   * offset from the top, crossAxis defines offset from the left side of the view port.
   *
   * A number is a shortcut for setting the mainAxis.
   */
  offset?: Offset;

  /**
   * Specifies the ARIA role type. Depending on how the dialog is used in the page, the developer
   * should choose from the following:
   * <ul>
   *   <li><code>dialog</code> defines an application window that is designed to interrupt the current
   *      processing of an application in order to prompt the user to enter information or require
   *      a response.</li>
   *   <li><code>alertdialog</code> defines type of dialog that contains an alert message, where initial
   *      focus goes to an element within the dialog.</li>
   * </ul>
   */
  role?: 'dialog' | 'alertdialog';

  /**
   * Optional <code>aria-labelledby</code> property referring to the content to be used as Dialog's
   * accessible <label htmlFor="" className=""></label>
   * If not specified, the Dialog will automatically refer to the entire header content as the accessible label.
   */
  'aria-labelledby'?: string;

  /**
   * Optional accessible description for the dialog.
   */
  'aria-describedby'?: string;
} & TestIdProps;

// dialog header

type DialogHeaderProps = Pick<DialogProps, 'header'> & {
  id?: string;
};

const StyledDialogHeader = ({ header, id }: DialogHeaderProps) => {
  const { styles } = useComponentTheme<never, DialogStyles>(DialogRedwoodTheme);
  const content: ComponentChild =
    typeof header === 'string' ? (
      <h1 id={id} className={styles.dialogTitleStyle}>
        {header}
      </h1>
    ) : (
      header
    );

  return <div className={styles.dialogHeaderStyle}>{content}</div>;
};

// styled dialog

type MainProps = Pick<DialogProps, 'children' | 'header' | 'footer' | 'role'> &
  Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'onKeyDown'> & {
    labelId?: string;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
  } & TestIdProps;

const StyledDialog = forwardRef(
  (
    {
      children,
      header,
      footer,
      labelId,
      role,
      ariaLabelledBy,
      ariaDescribedBy,
      onKeyDown,
      testId
    }: MainProps,
    ref: ForwardedRef<HTMLDivElement | null>
  ) => {
    const { classes: styleClasses, styles } = useComponentTheme<never, DialogStyles>(
      DialogRedwoodTheme
    );

    const testIdProps = useTestId(testId);

    return (
      <div
        ref={ref}
        className={classNames([styleClasses, globalImages])}
        style={{ opacity: 0 }}
        onKeyDown={onKeyDown}
        role={role}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        {...testIdProps}>
        <div className={styles.dialogContainerStyle}>
          <StyledDialogHeader id={labelId} header={header} />
          <div className={styles.dialogContentStyle}>
            <div className={styles.dialogBodyStyle}>{children}</div>
          </div>
          <div className={styles.dialogFooterStyle}>{footer}</div>
        </div>
      </div>
    );
  }
);

// main Dialog component

/**
 * WAI-ARIA-compliant dialog component. A dialog is a floating window that typically contains
 * a title bar and a content area.
 * The dialog is generally placed on top of the rest of the page content. It can be either modal
 * (only the content in the dialog can be interacted with) or non-modal (it's still possible
 * to interact with content outside of the dialog).
 */
export const Dialog = ({
  autoFocusRef,
  children,
  isOpen,
  header = '',
  footer,
  modality = 'modal',
  placement = 'center',
  offset = 0,
  onClose,
  onTransitionEnd,
  role = 'dialog',
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  testId
}: DialogProps) => {
  const [state, setState] = useState<Status>(isOpen ? 'opening' : 'unmounted');

  const uniqueId = useId();
  const labelId = ariaLabelledBy ? ariaLabelledBy : uniqueId;

  useEffect(() => {
    // Ignore state update on initial render
    if (state === 'unmounted' && !isOpen) {
      return;
    }
    setState(isOpen ? 'opening' : 'closing');
  }, [isOpen, state]);

  const { nodeRef } = useAnimation<Status, HTMLDivElement>(state, {
    animationStates,
    isAnimatedOnMount: true,
    onAnimationEnd: ({ animationState }) => {
      if (animationState === 'closing') {
        setState('unmounted');
        onTransitionEnd?.(false);
      } else {
        onTransitionEnd?.(true);
      }
    }
  });

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.code) {
      case 'Escape': {
        onClose?.({ reason: 'escapeKey' });
        break;
      }
    }
  };

  const renderDialog = () => {
    return (
      <WindowOverlay placement={placement} offset={offset}>
        <FocusTrap autoFocusRef={autoFocusRef}>
          <StyledDialog
            ref={nodeRef}
            labelId={labelId}
            header={header}
            footer={footer}
            role={role}
            ariaLabelledBy={labelId}
            ariaDescribedBy={ariaDescribedBy}
            onKeyDown={handleKeyDown}
            testId={testId}>
            {children}
          </StyledDialog>
        </FocusTrap>
      </WindowOverlay>
    );
  };

  if (modality === 'modal') {
    return <Modal isOpen={state !== 'unmounted'}>{renderDialog()}</Modal>;
  } else {
    return state !== 'unmounted' ? <Layer>{renderDialog()}</Layer> : null;
  }
};

const animationStates: UseAnimationConfig<Status, HTMLElement>['animationStates'] = {
  opening: {
    from: {
      scaleX: 0,
      scaleY: 0,
      transformOrigin: 'center',
      opacity: 0
    },
    to: {
      scaleX: 1,
      scaleY: 1,
      opacity: 1
    },
    options: {
      duration: 200
    }
  },
  closing: {
    from: {
      scaleX: 1,
      scaleY: 1,
      transformOrigin: 'center',
      opacity: 1
    },
    to: {
      scaleX: 0,
      scaleY: 0,
      opacity: 0
    },
    options: {
      duration: 200
    }
  }
};
